import { hasAnyRole, type CurrentUser, type RequestStatus, type StepKey, type StepStatus } from '@lg/shared';

/**
 * Request lifecycle (spec 7), gate 1 (spec 8.2) and the provisioning step
 * planner (spec 11.2). Pure functions; the service applies the results.
 */

// ── Gate 1 ───────────────────────────────────────────────────────────────────

export interface Gate1Input {
  answerErrorCount: number;
  idAvailability: { available: boolean; reason?: string };
  peopleStatus: string | null;
}

export interface GateResult {
  passed: boolean;
  reasons: string[];
}

export function evaluateGate1(input: Gate1Input): GateResult {
  const reasons: string[] = [];
  if (input.answerErrorCount > 0) {
    reasons.push(
      `${input.answerErrorCount} intake answer${input.answerErrorCount > 1 ? 's need' : ' needs'} attention`,
    );
  }
  if (!input.idAvailability.available) {
    reasons.push(`Legal entity ID is not available: ${input.idAvailability.reason ?? 'unknown reason'}`);
  }
  if (input.peopleStatus !== 'Valid') {
    reasons.push(
      input.peopleStatus
        ? `People and approvals import is ${input.peopleStatus}`
        : 'No people and approvals import yet',
    );
  }
  return { passed: reasons.length === 0, reasons };
}

// ── State machine ────────────────────────────────────────────────────────────

export type LifecycleAction =
  | { type: 'submit' }
  | { type: 'approve'; gate: 2 | 3 }
  | { type: 'sendBack'; gate: 2 | 3; comment: string }
  | { type: 'confirm'; typedId: string }
  | { type: 'cancel' }
  | { type: 'stepFailed' }
  | { type: 'resume' }
  | { type: 'stepsDone' }
  | { type: 'complete' };

export interface LifecycleRequest {
  status: RequestStatus;
  currentGate: number;
  requesterUpn: string;
  legalEntityId: string | null;
  version: number;
}

export interface LifecycleContext {
  /** Gate 1 evaluated now (submit and confirm) */
  gate1?: GateResult;
  /** Approvals already recorded for this request */
  approvals?: ReadonlyArray<{ gate: number; decision: string; requestVersion: number }>;
  /** Template dry run is newer than the last template change */
  dryRunFresh?: boolean;
  allTasksDone?: boolean;
  checksPass?: boolean;
}

export interface TransitionResult {
  next: { status: RequestStatus; currentGate: number } | null;
  errors: string[];
}

const SYSTEM = 'system';
const ok = (status: RequestStatus, currentGate: number): TransitionResult => ({
  next: { status, currentGate },
  errors: [],
});
const refuse = (...errors: string[]): TransitionResult => ({ next: null, errors });

/** Gate 2 approved for the request's current version. */
export function gate2Current(req: LifecycleRequest, ctx: LifecycleContext): boolean {
  const latest = (ctx.approvals ?? []).filter((a) => a.gate === 2 && a.requestVersion === req.version).at(-1);
  return latest?.decision === 'Approved';
}

export function transition(
  req: LifecycleRequest,
  action: LifecycleAction,
  actor: CurrentUser | typeof SYSTEM,
  ctx: LifecycleContext = {},
): TransitionResult {
  const isSystem = actor === SYSTEM;
  const user = isSystem ? null : actor;
  const role = (...roles: Parameters<typeof hasAnyRole>[1]): boolean => !!user && hasAnyRole(user, roles);
  const isOwner = !!user && user.upn === req.requesterUpn;

  switch (action.type) {
    case 'submit':
      if (req.status !== 'Draft')
        return refuse(`Only Draft requests can be submitted (this one is ${req.status})`);
      if (!role('Requester') || !isOwner) return refuse('Only the requester can submit');
      if (!ctx.gate1?.passed) return refuse(...(ctx.gate1?.reasons ?? ['Gate 1 was not evaluated']));
      return ok('InReview', 2);

    case 'approve':
      if (req.status !== 'InReview') return refuse('Only requests in review can be approved');
      if (action.gate === 2) {
        if (!role('FinanceApprover')) return refuse('Gate 2 needs the FinanceApprover role');
        if (req.currentGate !== 2) return refuse('Gate 2 is already approved for this version');
        return ok('InReview', 3);
      }
      if (!role('D365Admin')) return refuse('Gate 3 needs the D365Admin role');
      if (!gate2Current(req, ctx)) return refuse('Gate 2 must be approved for the current version first');
      if (!ctx.dryRunFresh)
        return refuse('The template dry run is older than the last template change; re-run it first');
      return ok('Approved', 4);

    case 'sendBack':
      if (req.status !== 'InReview' && req.status !== 'Approved') return refuse('Nothing to send back');
      if (!action.comment.trim()) return refuse('A comment is required to send a request back');
      if (action.gate === 2 && !role('FinanceApprover'))
        return refuse('Gate 2 needs the FinanceApprover role');
      if (action.gate === 3 && !role('D365Admin')) return refuse('Gate 3 needs the D365Admin role');
      return ok('Draft', 1);

    case 'confirm':
      if (req.status !== 'Approved') return refuse('Only approved requests can be confirmed');
      if (!role('D365Admin')) return refuse('Gate 4 needs the D365Admin role');
      if (action.typedId.trim().toUpperCase() !== (req.legalEntityId ?? '')) {
        return refuse(`Type the legal entity ID ${req.legalEntityId ?? ''} exactly to confirm`);
      }
      if (!ctx.gate1?.passed) return refuse(...(ctx.gate1?.reasons ?? ['Gate 1 was not evaluated']));
      return ok('Provisioning', 4);

    case 'cancel':
      if (!['Draft', 'Submitted', 'InReview', 'Approved'].includes(req.status)) {
        return refuse('Requests cannot be cancelled once provisioning has started');
      }
      if (!(isOwner || role('D365Admin'))) return refuse('Only the requester or a D365 admin can cancel');
      return ok('Cancelled', req.currentGate);

    case 'stepFailed':
      if (!isSystem) return refuse('Only the provisioning engine reports step failures');
      if (req.status !== 'Provisioning') return refuse('Not provisioning');
      return ok('NeedsAttention', req.currentGate);

    case 'resume':
      if (req.status !== 'NeedsAttention') return refuse('Only runs that need attention can resume');
      if (!role('D365Admin')) return refuse('Resume needs the D365Admin role');
      return ok('Provisioning', req.currentGate);

    case 'stepsDone':
      if (!isSystem) return refuse('Only the provisioning engine finishes steps');
      if (req.status !== 'Provisioning') return refuse('Not provisioning');
      return ok('Validating', req.currentGate);

    case 'complete':
      if (req.status !== 'Validating') return refuse('Only validating requests can be completed');
      if (!role('D365Admin')) return refuse('Completing needs the D365Admin role');
      if (!ctx.allTasksDone) return refuse('Every manual task must be Done');
      if (!ctx.checksPass) return refuse('Every validation check must pass');
      return ok('Ready', req.currentGate);
  }
}

// ── Step planner ─────────────────────────────────────────────────────────────

export const PROVISION_PLAN: readonly StepKey[] = [
  'preflight',
  'createLegalEntity',
  'addressAndRegistration',
  'copyFromTemplate',
  'venueOverrides',
  'importPeople',
  'assignSecurity',
  'importCustomersVendors',
  'importProjects',
  'policiesAndParameters',
  'importWorkflows',
  'validate',
  'notify',
];

export interface StepState {
  stepKey: StepKey;
  status: StepStatus;
}

/** Next step to run, `done` when all finished, `blocked` when a step failed. */
export function planNext(
  steps: readonly StepState[],
  plan: readonly StepKey[],
): StepKey | 'done' | 'blocked' {
  for (const key of plan) {
    const s = steps.find((x) => x.stepKey === key);
    if (!s || s.status === 'Pending' || s.status === 'Running') return key;
    if (s.status === 'Failed') return 'blocked';
  }
  return 'done';
}
